import clsx from "clsx";
import styles from "./Switch.module.scss";

export interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** REQUIRED. The control renders no text of its own, so without this it has no accessible name at all. */
  label: string;
  disabled?: boolean;
  /**
   * `md` (default) is the 44×26 control the guide's 44px touch minimum asks for. `sm` is 36×24 — a DENSE
   * DESKTOP rung for a switch that sits in a worksheet header bar beside 11px mono chrome, where the full
   * control overpowers the label it belongs to. 24px still clears WCAG 2.5.8 AA (24×24); like `Button`'s
   * `xs`, going under the guide's number is the CALLER's decision, so phones keep `md`.
   */
  size?: "md" | "sm";
  className?: string;
}

/**
 * A binary on/off switch: a pill track with a knob that sits LEFT when off and RIGHT when on.
 *
 * **State is carried by position and fill, never by words.** The knob's side says which way it is set and the
 * track is grey off / crimson on — so the control needs no "ON"/"OFF" text inside it, which is what the core
 * copy of this used to render. That also keeps it honest for colour-vision: position alone distinguishes the
 * two states with no reliance on hue.
 *
 * **Not a checkbox.** A checkbox is a value you submit with a form; a switch takes effect when you flip it.
 * `role="switch"` + `aria-checked` is the difference, and `label` is required because there is no visible text
 * to fall back on.
 */
export function Switch({ checked, onChange, label, disabled, size = "md", className }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={clsx(styles.track, className)}
      data-size={size}
      data-on={checked || undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className={styles.knob} aria-hidden="true" />
    </button>
  );
}
